const HEX_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i

export function isValidHex (value) {
  return typeof value === 'string' && HEX_PATTERN.test(value.trim())
}

export function sanitizeHex (value, fallback) {
  return isValidHex(value) ? value.trim() : fallback
}

function toRgb (hex) {
  const clean = hex.replace('#', '')
  const expanded = clean.length === 3
    ? clean.split('').map(c => c + c).join('')
    : clean
  const num = parseInt(expanded, 16)
  if (Number.isNaN(num)) return null
  return [num >> 16, (num >> 8) & 0xff, num & 0xff]
}

export function darkenHex (hex, amount) {
  const rgb = toRgb(hex)
  if (!rgb) return hex
  return '#' + rgb
    .map(v => Math.max(0, v - amount))
    .map(v => ('0' + v.toString(16)).slice(-2))
    .join('')
}

function lightenHex (hex, amount) {
  const rgb = toRgb(hex)
  if (!rgb) return hex
  return '#' + rgb
    .map(v => Math.min(255, v + amount))
    .map(v => ('0' + v.toString(16)).slice(-2))
    .join('')
}

const AA_NORMAL_TEXT = 4.5

function luminance (rgb) {
  const [r, g, b] = rgb.map(v => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function contrastRatio (background, foreground) {
  const lighter = Math.max(background, foreground)
  const darker = Math.min(background, foreground)
  return (lighter + 0.05) / (darker + 0.05)
}

function clearsNormalTextContrast (background, foreground) {
  const backgroundRgb = toRgb(background)
  const foregroundRgb = toRgb(foreground)
  return backgroundRgb && foregroundRgb &&
    contrastRatio(luminance(backgroundRgb), luminance(foregroundRgb)) >= AA_NORMAL_TEXT
}

// WCAG 2.1 AA for normal text is 4.5:1. Prefer the widget's light and dark
// label colors when one of them clears that bar on every background. Otherwise
// use black or white, whichever contrasts more. A mid-gray practice color
// fails both brand labels, and a label that only clears the rest color can
// fail once the button darkens on hover.
export function contrastTextColor (hex, { onDark = '#fff', onLight = '#313b59', backgrounds } = {}) {
  const colors = backgrounds && backgrounds.length ? backgrounds : [hex]
  const levels = colors.map(color => {
    const rgb = toRgb(color)
    return rgb ? luminance(rgb) : null
  })
  if (levels.some(level => level == null)) return onDark
  const ratio = foreground => {
    const foregroundLevel = luminance(toRgb(foreground))
    return Math.min(...levels.map(level => contrastRatio(level, foregroundLevel)))
  }
  if (ratio(onDark) >= AA_NORMAL_TEXT) return onDark
  if (ratio(onLight) >= AA_NORMAL_TEXT) return onLight
  return ratio('#000') >= ratio('#fff') ? '#000' : '#fff'
}

// A single label color must clear AA at rest and on hover. If darkening the
// background would break that requirement (for example #7c7c7c with black
// text), lighten the hover instead. Returning the rest color is the safe
// fallback when neither direction is usable.
export function primaryButtonTheme (hex) {
  const textColor = contrastTextColor(hex)
  const darkerHover = darkenHex(hex, 15)
  if (clearsNormalTextContrast(darkerHover, textColor)) {
    return { textColor, hoverColor: darkerHover }
  }

  const lighterHover = lightenHex(hex, 15)
  if (clearsNormalTextContrast(lighterHover, textColor)) {
    return { textColor, hoverColor: lighterHover }
  }

  return { textColor, hoverColor: hex }
}

const SECONDARY_HOVER = '#f0f2f7'

// Secondary labels sit on white and on #f0f2f7. Keep the practice color when
// it clears the hover fill. Otherwise use the same dark fallback as a light button.
export function secondaryLabelColor (practiceHex) {
  const practice = toRgb(practiceHex)
  const hover = toRgb(SECONDARY_HOVER)
  if (practice && hover && contrastRatio(luminance(hover), luminance(practice)) >= AA_NORMAL_TEXT) {
    return practiceHex
  }
  return contrastTextColor(SECONDARY_HOVER)
}
